import React, { useEffect, useRef, useState } from "react";
import { extractVideoThumbs } from "@/lib/videoThumbs";

export const fmtClock = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
};

const MIN_LEN = 1;
const THUMBS = 10;
const round1 = (x) => Math.round(x * 10) / 10;

// Lot 613 — piste de découpe à deux poignées (début / fin) : la fenêtre se déplace, chaque poignée s'étire
// entre 1 s et maxSec. Partagée par l'éditeur plein écran (sombre) et la feuille PC (claire).
// Lot 615 — `thumbsSrc` (URL locale de la vidéo) → vignettes réparties sur la durée en fond de piste.
export const VideoTrimTrack = ({ videoDur, maxSec, start, len, onChange, dark = false, thumbsSrc = null, testId = "story-trim" }) => {
  const trackRef = useRef(null);
  const modeRef = useRef(null); // "start" | "end" | "move" | null
  const grabRef = useRef(0);
  const [thumbs, setThumbs] = useState([]);
  const dur = Math.max(videoDur || 0, 0.1);
  const end = Math.min(dur, start + len);
  useEffect(() => {
    setThumbs([]);
    if (!thumbsSrc || !videoDur) return undefined;
    let alive = true;
    extractVideoThumbs(thumbsSrc, THUMBS, { duration: videoDur, width: 96, cancelled: () => !alive }).then((arr) => { if (alive) setThumbs(arr); });
    return () => { alive = false; };
  }, [thumbsSrc, videoDur]);
  const timeAt = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * dur;
  };
  const apply = (clientX) => {
    const t = timeAt(clientX);
    const mode = modeRef.current;
    if (mode === "start") {
      const s = Math.min(Math.max(t, Math.max(0, end - maxSec)), end - MIN_LEN);
      onChange({ start: round1(Math.max(0, s)), len: round1(end - Math.max(0, s)) });
    } else if (mode === "end") {
      const e = Math.max(Math.min(t, Math.min(dur, start + maxSec)), start + MIN_LEN);
      onChange({ start, len: round1(Math.min(dur, e) - start) });
    } else {
      const s = Math.min(Math.max(0, t - grabRef.current), Math.max(0, dur - len));
      onChange({ start: round1(s), len });
    }
  };
  const begin = (mode) => (e) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    modeRef.current = mode;
    const t = timeAt(e.clientX);
    if (mode === "move") grabRef.current = t >= start && t <= end ? t - start : len / 2;
    apply(e.clientX);
  };
  const move = (e) => { if (modeRef.current) apply(e.clientX); };
  const stop = () => { modeRef.current = null; };
  const pct = (x) => `${(x / dur) * 100}%`;
  const primary = "var(--yalola-primary)";
  const handleCls = "absolute top-0 bottom-0 w-4 max-w-[34%] grid place-items-center touch-none";
  const grip = <span className="block w-0.5 h-4 rounded-full bg-white/90" aria-hidden />;
  return (
    <div>
      <div
        ref={trackRef}
        className={`relative h-12 rounded-lg select-none touch-none overflow-hidden ${dark ? "bg-white/10 border border-white/15" : "cursor-pointer"}`}
        style={dark ? undefined : { background: "var(--yalola-surface-alt)", border: "1px solid var(--yalola-border)" }}
        onPointerDown={begin("move")} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop}
        data-testid={`${testId}-track`} data-thumbs={thumbs.length}
      >
        {thumbs.length > 0 && (
          <div className="absolute inset-0 flex pointer-events-none" aria-hidden data-testid={`${testId}-thumbs`}>
            {thumbs.map((u, i) => <img key={i} src={u} alt="" draggable={false} className="h-full flex-1 min-w-0 object-cover" style={{ opacity: dark ? 0.85 : 0.9 }} />)}
          </div>
        )}
        {Array.from({ length: 9 }).map((_, i) => <span key={i} className={`absolute top-2 bottom-2 w-px ${dark ? "bg-white/15" : ""}`} style={dark ? { left: `${(i + 1) * 10}%` } : { left: `${(i + 1) * 10}%`, background: "var(--yalola-border)" }} aria-hidden />)}
        {thumbs.length > 0 && (
          <>
            <div className="absolute top-0 bottom-0 left-0 bg-black/55 pointer-events-none" style={{ width: pct(start) }} aria-hidden />
            <div className="absolute top-0 bottom-0 right-0 bg-black/55 pointer-events-none" style={{ width: pct(Math.max(0, dur - end)) }} aria-hidden />
          </>
        )}
        <div className="absolute top-0 bottom-0 rounded-lg border-2" style={{ left: pct(start), width: pct(Math.max(end - start, 0.01)), borderColor: primary, background: thumbs.length ? "transparent" : `color-mix(in srgb, ${primary} ${dark ? 30 : 25}%, transparent)` }} data-testid={`${testId}-window`} data-start={start} data-len={round1(end - start)}>
          <div className={`${handleCls} left-0 rounded-l-md cursor-ew-resize`} style={{ background: primary }} onPointerDown={begin("start")} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} role="slider" aria-label="start" aria-valuenow={start} data-testid={`${testId}-handle-start`}>{grip}</div>
          <div className={`${handleCls} right-0 rounded-r-md cursor-ew-resize`} style={{ background: primary }} onPointerDown={begin("end")} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} role="slider" aria-label="end" aria-valuenow={end} data-testid={`${testId}-handle-end`}>{grip}</div>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-bold px-1 py-0.5 rounded text-white whitespace-nowrap pointer-events-none" style={{ background: primary }}>{Math.round(end - start)} s</span>
        </div>
      </div>
      <div className={`flex justify-between text-[10px] mt-1 ${dark ? "text-white/60" : ""}`} style={dark ? undefined : { color: "var(--yalola-text-muted)" }}>
        <span>00:00</span><span>{fmtClock(dur)}</span>
      </div>
    </div>
  );
};

export default VideoTrimTrack;
